import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

// Components
import OfferCoveragesContainer from './OfferCoveragesContainer';
import OfferGeneralInfo from './OfferGeneralInfo';
import OfferOptionContainer from './offer-options/OfferOptionContainer';

// Types
import { getCoveragesGroups } from '../../services/productFactoryService';
import {
  FormulasV2,
  isOfferFailed,
  isOfferSuccessfull,
  LouiseOffer,
  Warning,
} from '../../../../../types/Quotes';
import {
  HStack,
  Skeleton,
  SkeletonCircle,
  VStack,
  Stack,
  Box,
  SimpleGrid,
  Divider,
} from '@wegroup/design-system';
import { OfferState } from '../../services/offersService';
import { setOfferSelectedGuarantees } from '../../store/redux/slices/offersSlice';
import { useLouiseDispatch, useLouiseSelector } from '../../store/redux/store';
import OfferFormulas from './offer-options/OfferFormulas';
import OfferBenefit from './offer-options/OfferBenefit';

const OfferRatingSkeleton: React.FC<React.PropsWithChildren<unknown>> = () => (
  <HStack spacing="2" align="flex-start">
    <SkeletonCircle size="5" />
    <VStack spacing="2" align="flex-start">
      <Skeleton height="4" w="40" />
      <Skeleton height="4" w="24" />
    </VStack>
  </HStack>
);

const OpenOptionsSkeleton: React.FC<React.PropsWithChildren<unknown>> = () => (
  <>
    <OfferSectionDivider />
    <InfoLayout>
      <Label>
        <Skeleton h="4" w="64" />
      </Label>
      <SimpleGrid columns={2} gap="4">
        <OfferRatingSkeleton />
        <OfferRatingSkeleton />
        <OfferRatingSkeleton />
        <OfferRatingSkeleton />
        <OfferRatingSkeleton />
        <OfferRatingSkeleton />
      </SimpleGrid>
    </InfoLayout>
  </>
);

interface OptionsProps {
  offer: LouiseOffer;
  onOptionsChange: (arg0: string[]) => void;
  small: boolean;
}

const Options: React.FC<React.PropsWithChildren<OptionsProps>> = ({
  offer,
  onOptionsChange,
  small,
}) => {
  const { t } = useTranslation();

  const [formulas, setFormulas] = useState<FormulasV2>();
  const [warnings, setWarnings] = useState<Warning[]>();

  useEffect(() => {
    if (offer.state === OfferState.FETCHED && isOfferSuccessfull(offer)) {
      // Sets the initial values if the quote was ever retrieved
      setFormulas(offer.details.formulas_v2);
      setWarnings(offer.details.warnings);
    }
  }, [offer]);

  return (
    <Box w="100%">
      <Label>{t('SALES-CONVERSATION_OFFERS_SINGLE-OFFER_OPTIONS')}</Label>
      <OfferOptionContainer offer={offer} onOptionsChange={onOptionsChange} />

      {formulas && (
        <Box mt="4">
          <Label>{t('Formulas')}</Label>
          <OfferFormulas offer={offer} formulas={formulas} />
        </Box>
      )}

      {warnings && warnings?.length > 0 && (
        <Box mt="6">
          <OfferBenefit benefits={warnings} />
        </Box>
      )}
    </Box>
  );
};

interface Props {
  offer: LouiseOffer;
  isOpen: boolean;
  onOptionsChange: (arg0: string[]) => void;
}

const SingleOfferInfoContainer: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
  isOpen,
  onOptionsChange,
}) => {
  const { t } = useTranslation();

  const dispatch = useLouiseDispatch();
  const activeCarQuoteFilter = useLouiseSelector(
    (state) => state.offers.activeCarQuoteFilter,
  );
  const selectedGuarantees: string[] =
    useLouiseSelector(
      (state) =>
        state.offers.offerSelectedGuarantees[offer.info.insurance_type]?.[
          activeCarQuoteFilter
        ]?.[offer.info.company.key],
    ) || offer.selected_guarantees;

  useEffect(() => {
    // Hardcode automatic nature set
    if (
      offer.state === OfferState.FETCHED &&
      offer.info.insurance_type === 'home'
    ) {
      if (offer.selected_guarantees.includes('nature')) {
        dispatch(
          setOfferSelectedGuarantees({
            riskObject: offer.info.insurance_type,
            company: offer.info.company.key,
            value: [...selectedGuarantees, 'nature'],
          }),
        );
      } else if (
        selectedGuarantees.includes('nature') &&
        !offer.selected_guarantees.includes('nature')
      ) {
        dispatch(
          setOfferSelectedGuarantees({
            riskObject: offer.info.insurance_type,
            company: offer.info.company.key,
            value: selectedGuarantees.filter((name) => name !== 'nature'),
          }),
        );
      }
    }
  }, [offer.state]);

  if (isOfferFailed(offer)) {
    return (
      <Container>
        <OfferGeneralInfo offer={offer} />
        <Stack
          p="7"
          spacing="6"
          align="stretch"
          direction={{ base: 'column', laptopL: 'row' }}
          minH="32"
        >
          <Options
            offer={offer}
            small={!isOpen}
            onOptionsChange={onOptionsChange}
          />
        </Stack>
      </Container>
    );
  }

  if (offer.state === OfferState.LOADING)
    return (
      <Container>
        <OfferGeneralInfo offer={offer} />
        <Stack
          p="7"
          spacing="6"
          align="stretch"
          direction={{ base: 'column', laptopL: isOpen ? 'column' : 'row' }}
          minH="32"
        >
          <Options
            offer={offer}
            small={!isOpen}
            onOptionsChange={onOptionsChange}
          />
        </Stack>
        {isOpen && <OpenOptionsSkeleton />}
      </Container>
    );

  // Will always return a value if quality is defined
  const [goodCoverages, badCoverages] = getCoveragesGroups(offer.quality);

  return (
    <Container>
      <OfferGeneralInfo offer={offer} />
      <Stack p="7" spacing="6" align="stretch" direction="column" minH="32">
        <Options
          offer={offer}
          onOptionsChange={onOptionsChange}
          small={!isOpen}
        />
        {!isOpen && goodCoverages && goodCoverages.length > 0 && (
          <Box flexGrow={1}>
            <Label>{t('Important for you')}</Label>
            <OfferCoveragesContainer coverages={goodCoverages} onlyIcons />
          </Box>
        )}
      </Stack>
      {isOpen && goodCoverages && badCoverages ? (
        <>
          {goodCoverages.length === 0 && badCoverages.length === 0 && (
            <NoCoverages />
          )}
          {goodCoverages.length > 0 && (
            <>
              <OfferSectionDivider />
              <InfoLayout>
                <Label>
                  {t('Some of the benefits that are important to you')}
                </Label>
                <OfferCoveragesContainer coverages={goodCoverages} />
              </InfoLayout>
            </>
          )}
          {badCoverages.length > 0 && (
            <>
              <OfferSectionDivider />
              <InfoLayout>
                <Label>
                  {t('What is less covered according to your needs')}
                </Label>
                <OfferCoveragesContainer coverages={badCoverages} />
              </InfoLayout>
            </>
          )}
        </>
      ) : (
        isOpen && <NoCoverages />
      )}
    </Container>
  );
};

const NoCoverages: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();

  return (
    <>
      <OfferSectionDivider />
      <InfoLayout>
        <Label>
          {t(
            'There is no data available about the quality of the coverages for this offer that are important to you',
          )}
        </Label>
      </InfoLayout>
    </>
  );
};

const OfferSectionDivider: React.FC<React.PropsWithChildren<unknown>> = () => (
  <Divider color="gray.50" mx="8" w="auto" opacity="1" />
);

const Container = styled.div`
  padding-bottom: 0.8rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const InfoLayout = styled.div`
  padding: 2.4rem 0;
  margin: 0 3.2rem;
  border-bottom: 1px solid ${({ theme }) => theme.gray[50]};
  &:last-of-type {
    border-bottom: unset;
  }
`;

const Label = styled.p`
  color: ${({ theme }) => theme.labels.guaranteeText};
  font-size: 1.6rem;
  line-height: 120%;
  margin-bottom: 1.6rem;
`;
export default SingleOfferInfoContainer;
